import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
    } catch (error) {
      localStorage.removeItem('adminToken');
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('adminToken', access_token);
      await verifyToken(access_token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setAdmin(null);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  /** Rewrite a `/api/files/<path>` URL to include the admin JWT as `?auth=`
   *  so `<img src>` / `<a href>` work without needing custom fetch code. */
  const signFileUrl = (url) => {
    if (!url) return url;
    const token = localStorage.getItem('adminToken');
    if (!token || !url.includes('/api/files/')) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}auth=${encodeURIComponent(token)}`;
  };

  return (
    <AuthContext.Provider value={{ admin, loading, login, logout, getAuthHeaders, signFileUrl }}>
      {children}
    </AuthContext.Provider>
  );
};
